/**
 * TodaysPulse — the daily-hook hero screen for premium users (FB-HOOK-1).
 *
 * 5 sections, Bloomberg × Robinhood dark aesthetic per /app/design_guidelines.json:
 *   1. Greeting hero ("Good morning, X. 4 things you track moved >2% overnight.")
 *   2. Top movers list (with inline 7d sparklines)
 *   3. Portfolio Health Score (0-100)
 *   4. Story of the Day (image hero + headline)
 *   5. Serendipity Pick (outside-tracklist surprise)
 *   6. Social proof strip ("X people watching")
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Sparkles, Eye, ArrowRight, Loader2 } from 'lucide-react';
import api from '@/api';
import openExternal from '@/utils/openExternal';

// Tiny inline SVG sparkline.
function Sparkline({ values = [], positive = true, width = 80, height = 28 }) {
  if (!values || values.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = width / (values.length - 1);
  const points = values
    .map((v, i) => `${i * stepX},${height - ((v - min) / range) * height}`)
    .join(' ');
  const stroke = positive ? '#10B981' : '#EF4444';
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={points} />
    </svg>
  );
}

function HealthGauge({ score = 50, headline = '' }) {
  const ringColor = score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';
  const r = 48;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="flex items-center gap-5" data-testid="portfolio-health">
      <div className="relative" style={{ width: 110, height: 110 }}>
        <svg width="110" height="110" className="transform -rotate-90">
          <circle cx="55" cy="55" r={r} stroke="#27272A" strokeWidth="6" fill="none" />
          <motion.circle
            cx="55" cy="55" r={r}
            stroke={ringColor} strokeWidth="6" fill="none" strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-mono font-medium text-white" data-testid="health-score">{score}</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">/100</span>
        </div>
      </div>
      <div className="flex-1">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1">Portfolio Health</p>
        <p className="text-sm text-neutral-300 leading-snug">{headline}</p>
      </div>
    </div>
  );
}

export default function TodaysPulse({ user, onOpenPortfolio, onTopicClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Stable per-tab session id used for the viewer-counter dedupe.
  const sessionId = useMemo(() => {
    let id = sessionStorage.getItem('wtf_session_id');
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('wtf_session_id', id);
    }
    return id;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getTodaysPulse()
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => {
        if (cancelled) return;
        const status = e?.response?.status;
        setErr(status === 402 ? 'premium' : 'error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Pulse the viewer counter for the user's top mover so they show up as
  // an active viewer on social-proof strips of OTHER users with the same holding.
  useEffect(() => {
    const top = data?.top_movers?.[0];
    if (!top?.symbol) return;
    api.trackEntityView(`asset:${top.symbol}`, sessionId).catch(() => {});
    const intv = setInterval(
      () => api.trackEntityView(`asset:${top.symbol}`, sessionId).catch(() => {}),
      4 * 60 * 1000, // re-pulse every 4 min so the 5-min TTL never expires while user is on screen
    );
    return () => clearInterval(intv);
  }, [data?.top_movers, sessionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: '#09090B' }}>
        <Loader2 className="animate-spin text-neutral-500" size={28} />
      </div>
    );
  }

  if (err === 'premium') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#09090B', color: '#FAFAFA' }}>
        <div className="text-center max-w-sm">
          <Sparkles className="mx-auto mb-4 text-emerald-400" size={32} />
          <h2 className="text-2xl font-black tracking-tighter mb-3">Today's Pulse is Premium.</h2>
          <p className="text-sm text-neutral-400 mb-6">Get a daily personalised hero screen with your top movers, portfolio health, and the one story you can't miss.</p>
          <button
            onClick={() => { window.location.href = '/?upgrade=1'; }}
            className="px-6 py-3 rounded-md bg-emerald-500 text-black font-bold uppercase text-xs tracking-[0.18em]"
          >
            See Premium
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#09090B', color: '#FAFAFA' }}>
        <p className="text-sm text-neutral-500">Today's Pulse couldn't load. Try again in a moment.</p>
      </div>
    );
  }

  const { greeting, top_movers, portfolio_health, story_of_the_day, serendipity, social_proof, tracked_count } = data;

  return (
    <div className="min-h-screen w-full pb-32 sm:pb-24" style={{ background: '#09090B', color: '#FAFAFA' }} data-testid="todays-pulse">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 sm:py-10">

        {/* Greeting */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="pulse-greeting">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400/80 mb-3">Today's Pulse</p>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tighter leading-tight">
            Good morning, {greeting?.name}.
          </h1>
          <p className="text-base sm:text-lg text-neutral-400 mt-3 max-w-2xl leading-snug font-mono">
            {greeting?.headline}
          </p>
        </motion.div>

        {/* Top Movers + Portfolio Health bento removed (May 2026 iter 62)
            so the "Open Dashboard →" CTA at the bottom rises above the
            bottom nav fold and is reachable without scrolling past empty
            placeholder copy. Both blocks were showing "Add holdings…"
            empty states for the majority of users. */}

        {/* Story of the Day */}
        {story_of_the_day && (
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            onClick={() => {
              if (story_of_the_day.id) onTopicClick?.({ id: story_of_the_day.id, ...story_of_the_day });
              else if (story_of_the_day.source_url) openExternal(story_of_the_day.source_url);
            }}
            className="mt-4 w-full text-left rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950 hover:border-zinc-600 transition group"
            data-testid="story-of-the-day"
          >
            <div className="relative aspect-[16/7] overflow-hidden">
              {story_of_the_day.image_url ? (
                <img src={story_of_the_day.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-zinc-800 via-zinc-900 to-black" />
              )}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(9,9,11,0.95), rgba(9,9,11,0.45) 60%, transparent)' }} />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-2">Story of the Day</p>
                <h3 className="text-xl sm:text-2xl font-bold leading-tight text-white">
                  {story_of_the_day.headline}
                </h3>
                {story_of_the_day.the_shift && (
                  <p className="text-sm text-neutral-300 mt-2 line-clamp-2">{story_of_the_day.the_shift}</p>
                )}
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-between text-xs text-neutral-500">
              <span>{story_of_the_day.source_label || story_of_the_day.category}</span>
              <span className="flex items-center gap-1 text-emerald-400 group-hover:gap-2 transition-all">
                Why this matters for you <ArrowRight size={12} />
              </span>
            </div>
          </motion.button>
        )}

        {/* Serendipity */}
        {serendipity && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="mt-4 rounded-lg border border-amber-500/30 bg-zinc-950 p-5"
            data-testid="serendipity"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-amber-400" />
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Outside your tracklist</p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (serendipity.id) {
                  onTopicClick?.({ id: serendipity.id, title: serendipity.entity_name, category: serendipity.category });
                } else if (serendipity.source_url) {
                  openExternal(serendipity.source_url);
                }
              }}
              className="text-left w-full"
            >
              <h3 className="text-lg font-bold text-white">{serendipity.entity_name}</h3>
              {serendipity.hook && <p className="text-sm text-neutral-400 mt-1 line-clamp-3">{serendipity.hook}</p>}
              <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                {serendipity.category && <span className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 uppercase tracking-wider">{serendipity.category}</span>}
                {serendipity.region && <span className="font-mono">{serendipity.region}</span>}
              </div>
            </button>
          </motion.div>
        )}

        {/* Social proof */}
        {social_proof?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-6 flex items-center gap-3 flex-wrap"
            data-testid="social-proof"
          >
            <Eye size={12} className="text-neutral-500" />
            {social_proof.map(sp => (
              <div key={sp.symbol} className="flex items-center gap-1.5 text-xs text-neutral-400 font-mono">
                <span className="font-bold text-white">{sp.symbol}</span>
                <span>·</span>
                <span>{sp.viewers} watching</span>
              </div>
            ))}
          </motion.div>
        )}

        {/* CTA — jumps the user to the Dashboard (richer multi-section feed) rather
            than the legacy Portfolio settings screen (FB-PERS-1.2 follow-up). */}
        <div className="mt-10">
          <button
            type="button"
            onClick={onOpenPortfolio}
            className="w-full py-3.5 rounded-md border border-zinc-800 hover:border-zinc-600 text-sm font-bold uppercase tracking-[0.18em] text-neutral-300 hover:text-white transition"
            data-testid="open-full-portfolio"
          >
            Open Dashboard →
          </button>
        </div>
      </div>
    </div>
  );
}
